/**
 * Task #1480 — engine puro di completezza riga per il Carico Merce.
 *
 * Usato sia dal frontend (banner/pill/wizard/gating conferma) sia dal
 * backend (gating server-side prima di committare il carico). Mai stato,
 * mai I/O: solo derivazione da `LineCompletenessInput`.
 */

import { z } from 'zod';

export const LINE_COMPLETENESS_ORIGINS = [
  'manual',
  'from_purchase_order',
  'from_ddt_passivo',
  'from_ai_import',
] as const;
export type LineCompletenessOrigin = (typeof LINE_COMPLETENESS_ORIGINS)[number];

/**
 * Token machine-readable dei campi mancanti su una riga. Mantenere stretto
 * — l'UI mappa ogni token a un'azione (apertura dialog, focus su cella,
 * eccetera).
 */
export const MISSING_FIELDS = [
  'product_link',
  'vat_rate',
  'unit_cost',
  'lot_number',
  'expiry_date',
  'serials_count',
  'serial_kinds_composition',
  'quantity_received_unconfirmed',
  'supplier_product_code',
] as const;
export type MissingField = (typeof MISSING_FIELDS)[number];

export type LineCompletenessStatus = 'complete' | 'incomplete' | 'blocked';

export interface LineCompleteness {
  status: LineCompletenessStatus;
  isComplete: boolean;
  missingFields: MissingField[];
  /** Motivo bloccante leggibile (riempito solo se `status='blocked'`). */
  blockingReason: string | null;
}

/**
 * Composizione kind richiesta per pezzo (es. IMEI×2 + ICCID×1). Replica
 * della shape esistente `SerialKindRequirement` lato shared types.
 */
export interface LineSerialKindRequirement {
  kind: string;
  count: number;
}

/** Seriale per pezzo (kind opzionale: derivato dall'articolo, non scelto per riga). */
export interface LineSerialEntry {
  kind?: string;
  value: string;
  ok: boolean;
}

/**
 * Forma minima della riga necessaria al calcolo. Il frontend accumula
 * molti più campi (autocomplete, tone, ecc.); qui prendiamo SOLO ciò che
 * impatta la completezza, così la funzione è facilmente unit-testabile e
 * il backend può chiamarla senza dipendere dal modello UI.
 */
export interface LineCompletenessInput {
  product_id: string | null;
  vat_rate_id?: string | null;
  vat_rate?: number | null;
  unit_price?: number | null;
  quantity: number;
  /** Quantità ordinata (solo per origine `from_purchase_order`). */
  quantity_ordered?: number | null;
  /** True se l'operatore ha confermato esplicitamente "ricevuto come ordinato". */
  receipt_confirmed?: boolean;
  serial_mode: 'none' | 'unique' | 'non_unique';
  serial_kinds_required: LineSerialKindRequirement[];
  serials: LineSerialEntry[];
  lot_number: string | null;
  expiry_date: string | null;
  /** Per origine DDT passivo: codice articolo fornitore non risolto a catalogo. */
  supplier_product_code?: string | null;
  /** True se il prodotto richiede tracciamento scadenza (es. food/farma). */
  requires_expiry?: boolean;
  /** True se il prodotto richiede lotto (auto-derived per `non_unique`). */
  requires_lot?: boolean;
}

export interface LineCompletenessOptions {
  origin: LineCompletenessOrigin;
}

/**
 * Calcola la completezza di una singola riga. Funzione pura: stesso input
 * ⇒ stesso output, nessuna allocazione superflua, nessun side-effect.
 */
export function computeLineCompleteness(
  line: LineCompletenessInput,
  options: LineCompletenessOptions,
): LineCompleteness {
  const missing: MissingField[] = [];
  let blockingReason: string | null = null;

  // (1) Collegamento al catalogo — sempre richiesto.
  if (!line.product_id) {
    missing.push('product_link');
    if (options.origin === 'from_ddt_passivo' && line.supplier_product_code) {
      // Codice fornitore non risolto: blocca con ponte al LinkSupplierCodeDialog.
      missing.push('supplier_product_code');
      blockingReason = `Codice fornitore "${line.supplier_product_code}" non collegato al catalogo`;
    }
  }

  // (2) Quantità positiva — sempre.
  if (!(line.quantity > 0)) {
    missing.push('quantity_received_unconfirmed');
  }

  // (3) IVA — richiesta sempre. Accettiamo sia `vat_rate_id` (snapshot
  //     fiscale post #1475) sia `vat_rate` numerico per back-compat.
  const hasVat = !!line.vat_rate_id || (typeof line.vat_rate === 'number' && line.vat_rate >= 0);
  if (!hasVat) missing.push('vat_rate');

  // (4) Prezzo costo — richiesto per ogni origine TRANNE PO ordini in cui
  //     il costo arriva normalizzato dal documento sorgente.
  if (options.origin !== 'from_purchase_order') {
    // Zero è un prezzo documentale/costo valido (omaggio o riga a valore
    // nullo); solo assenza, NaN e valori negativi rendono la riga incompleta.
    if (!(typeof line.unit_price === 'number' && Number.isFinite(line.unit_price) && line.unit_price >= 0)) {
      missing.push('unit_cost');
    }
  }

  // (5) Lotto — obbligatorio per L2 non_unique e per prodotti `requires_lot`.
  const needsLot = line.serial_mode === 'non_unique' || !!line.requires_lot;
  if (needsLot && !(line.lot_number && line.lot_number.trim().length > 0)) {
    missing.push('lot_number');
  }

  // (6) Scadenza — obbligatoria se `requires_expiry`.
  if (line.requires_expiry && !line.expiry_date) {
    missing.push('expiry_date');
  }

  // (7) Seriali per L2 unique: count + composizione kind per pezzo.
  if (line.serial_mode === 'unique') {
    const okSerials = line.serials.filter((s) => s.ok);
    const units = Math.max(0, Math.round(line.quantity));
    if (line.serial_kinds_required.length > 0) {
      const slotsPerUnit = line.serial_kinds_required.reduce(
        (s, k) => s + Math.max(1, k.count),
        0,
      );
      const expected = slotsPerUnit * units;
      if (okSerials.length !== expected) {
        missing.push('serials_count');
      } else {
        // Verifica composizione per ogni pezzo.
        const required = new Map<string, number>();
        for (const k of line.serial_kinds_required) required.set(k.kind, Math.max(1, k.count));
        let compositionOk = true;
        for (let u = 0; u < units && compositionOk; u++) {
          const chunk = okSerials.slice(u * slotsPerUnit, (u + 1) * slotsPerUnit);
          const got = new Map<string, number>();
          for (const s of chunk) {
            const k = s.kind ?? 'serial';
            got.set(k, (got.get(k) || 0) + 1);
          }
          required.forEach((count, kind) => {
            if ((got.get(kind) || 0) !== count) compositionOk = false;
          });
          if (compositionOk) {
            got.forEach((_v, k) => {
              if (!required.has(k)) compositionOk = false;
            });
          }
        }
        if (!compositionOk) missing.push('serial_kinds_composition');
      }
    } else if (okSerials.length !== units) {
      missing.push('serials_count');
    }
  }

  // (8) PO origin: se l'operatore non ha confermato la ricezione e qty
  //     differisce dall'ordinato, marcare come incompleto (richiede atto
  //     esplicito "Ricevuto come ordinato"/"Ricevuto parziale").
  if (
    options.origin === 'from_purchase_order'
    && !line.receipt_confirmed
    && typeof line.quantity_ordered === 'number'
    && line.quantity_ordered > 0
    && line.quantity !== line.quantity_ordered
  ) {
    missing.push('quantity_received_unconfirmed');
  }

  const status: LineCompletenessStatus =
    blockingReason ? 'blocked' : missing.length === 0 ? 'complete' : 'incomplete';

  return {
    status,
    isComplete: status === 'complete',
    missingFields: dedupe(missing),
    blockingReason,
  };
}

function dedupe<T>(arr: T[]): T[] {
  const out: T[] = [];
  const seen = new Set<T>();
  for (const x of arr) {
    if (!seen.has(x)) { seen.add(x); out.push(x); }
  }
  return out;
}

// ─── Aggregato di summary per il banner globale ────────────────────────────

export interface LineCompletenessSummary {
  totalLines: number;
  totalComplete: number;
  totalIncomplete: number;
  totalBlocked: number;
  byField: Record<MissingField, number>;
}

export function summarizeCompleteness(items: LineCompleteness[]): LineCompletenessSummary {
  const byField = MISSING_FIELDS.reduce((acc, f) => { acc[f] = 0; return acc; }, {} as Record<MissingField, number>);
  let complete = 0, incomplete = 0, blocked = 0;
  for (const c of items) {
    if (c.status === 'complete') complete++;
    else if (c.status === 'blocked') blocked++;
    else incomplete++;
    for (const f of c.missingFields) byField[f]++;
  }
  return {
    totalLines: items.length,
    totalComplete: complete,
    totalIncomplete: incomplete + blocked,
    totalBlocked: blocked,
    byField,
  };
}

// ─── Etichette IT per UI (allineate al gergo POS) ──────────────────────────

export const MISSING_FIELD_LABELS_IT: Record<MissingField, string> = {
  product_link: 'Articolo non collegato',
  vat_rate: 'IVA',
  unit_cost: 'Prezzo costo',
  lot_number: 'Lotto',
  expiry_date: 'Scadenza',
  serials_count: 'Seriali',
  serial_kinds_composition: 'Composizione seriali',
  quantity_received_unconfirmed: 'Quantità da confermare',
  supplier_product_code: 'Codice fornitore',
};

// ─── Zod schemas (boundary HTTP) ───────────────────────────────────────────

export const LineSerialKindRequirementSchema = z.object({
  kind: z.string().min(1),
  count: z.number().int().min(1),
});

export const LineSerialEntrySchema = z.object({
  kind: z.string().optional(),
  value: z.string(),
  ok: z.boolean(),
});

export const LineCompletenessInputSchema = z.object({
  product_id: z.string().nullable(),
  vat_rate_id: z.string().nullable().optional(),
  vat_rate: z.number().nullable().optional(),
  unit_price: z.number().nullable().optional(),
  quantity: z.number(),
  quantity_ordered: z.number().nullable().optional(),
  receipt_confirmed: z.boolean().optional(),
  serial_mode: z.enum(['none', 'unique', 'non_unique']),
  serial_kinds_required: z.array(LineSerialKindRequirementSchema),
  serials: z.array(LineSerialEntrySchema),
  lot_number: z.string().nullable(),
  expiry_date: z.string().nullable(),
  supplier_product_code: z.string().nullable().optional(),
  requires_expiry: z.boolean().optional(),
  requires_lot: z.boolean().optional(),
});

export const LineCompletenessOriginSchema = z.enum(LINE_COMPLETENESS_ORIGINS);
